import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../service/GetTemplate';
import { handlePutRequest } from '../service/PutTemplate';
import { handlePostRequest } from '../service/PostTemplate';
import { endPoints } from '../Config';
import { toast } from 'react-toastify';
import { LANGUAGE_OPTIONS, updateStoredAdminUserFields } from '../utilities/languagePreference';
import usePreferredLanguage from '../utilities/usePreferredLanguage';
import { translateUiText } from '../utilities/siteTranslations';

const Profile = () => {
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState(null);

    const [passwordDialogVisible, setPasswordDialogVisible] = useState(false);
    const [passwordStep, setPasswordStep] = useState('email'); // email -> otp -> newPassword
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    const dispatch = useDispatch();
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);

    const user = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const currentUserId = user?.data?.adminUser?._id || user?.data?.adminUser?.id;

    const validationSchema = Yup.object().shape({
        firstName: Yup.string()
            .trim()
            .matches(/^[^\s]+(\s[^\s]+)*$/, t('Leading and trailing space is not allowed'))
            .required(t('First name is required'))
            .min(2, t('First name must be at least 2 characters')),
        lastName: Yup.string()
            .trim()
            .matches(/^[^\s]+(\s[^\s]+)*$/, t('Leading and trailing space is not allowed'))
            .required(t('Last name is required'))
            .min(2, t('Last name must be at least 2 characters')),
        email: Yup.string().trim().email(t('Invalid email')).required(t('Email is required')),
        whatsApp: Yup.string()
            .trim()
            .matches(/^\d+$/, t('Phone number should only contain numbers'))
            .required(t('Phone number is required'))
            .min(10, t('Phone number must be at least 10 digits'))
    });

    const formik = useFormik({
        validationSchema,
        enableReinitialize: true,
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            whatsApp: '',
            preferredLanguage: 'es',
            role: '',
            isActive: '',
            userId: '',
            createdAt: '',
            updatedAt: ''
        },
        onSubmit: async (values, { setSubmitting }) => {
            setLoading(true);
            try {
                if (!currentUserId) {
                    toast.error(t('User ID not found. Please log in again.'));
                    setLoading(false);
                    setSubmitting(false);
                    return;
                }

                const updateData = {
                    userId: values?.userId,
                    firstName: values.firstName || '',
                    lastName: values.lastName || '',
                    email: values.email || '',
                    role: user?.data?.adminUser?.role?._id || '',
                    whatsApp: values.whatsApp || '',
                    preferredLanguage: values.preferredLanguage || 'es'
                };

                const response = await dispatch(handlePutRequest(updateData, endPoints.updateUser, true));
                if (response?.status === 200) {
                    // Refresh profile data so UI updates only after successful API call
                    const refreshed = await handleGetRequest(endPoints.getUserProfile(currentUserId), true);
                    if (refreshed?.data) {
                        const localAdmin = user?.data?.adminUser || {};
                        const mergedUser = {
                            ...refreshed.data,
                            createdAt: refreshed.data.createdAt || localAdmin.createdAt,
                            updatedAt: refreshed.data.updatedAt || localAdmin.updatedAt
                        };

                        setUserData(mergedUser);
                        updateStoredAdminUserFields({
                            firstName: mergedUser.firstName || '',
                            lastName: mergedUser.lastName || '',
                            email: mergedUser.email || '',
                            whatsApp: mergedUser.whatsApp || '',
                            preferredLanguage: mergedUser.preferredLanguage || 'es'
                        });
                        formik.setFieldValue('firstName', mergedUser.firstName || '');
                        formik.setFieldValue('lastName', mergedUser.lastName || '');
                        formik.setFieldValue('email', mergedUser.email || '');
                        formik.setFieldValue('whatsApp', mergedUser.whatsApp || '');
                        formik.setFieldValue('preferredLanguage', mergedUser.preferredLanguage || 'es');
                        const apiRoleTitle =
                            typeof mergedUser.role === 'object' && mergedUser.role !== null ? mergedUser.role.title : '';
                        const newRoleTitle = apiRoleTitle || formik.values.role || localAdmin.role?.title || '';
                        formik.setFieldValue('role', newRoleTitle);
                        formik.setFieldValue('isActive', mergedUser.isActive || '');
                        formik.setFieldValue('userId', mergedUser._id || '');
                        formik.setFieldValue('createdAt', mergedUser.createdAt || '');
                        formik.setFieldValue('updatedAt', mergedUser.updatedAt || '');
                    }
                } else {
                    toast.error(t('Failed to update profile. Please try again.'));
                }
            } catch (error) {
                toast.error(t('An error occurred while updating profile. Please try again.'));
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        }
    });

    useEffect(() => {
        const fetchUserData = async () => {
            if (!currentUserId) {
                return;
            }

            // 1) Immediately hydrate from localStorage to avoid empty UI while API loads
            const localAdmin = user?.data?.adminUser || null;
            if (localAdmin && !userData) {
                setUserData(localAdmin);
                formik.setFieldValue('firstName', localAdmin.firstName || '');
                formik.setFieldValue('lastName', localAdmin.lastName || '');
                formik.setFieldValue('email', localAdmin.email || '');
                formik.setFieldValue('whatsApp', localAdmin.whatsApp || '');
                formik.setFieldValue('preferredLanguage', localAdmin.preferredLanguage || 'es');
                formik.setFieldValue('role', localAdmin.role?.title || '');
                formik.setFieldValue('isActive', localAdmin.isActive || '');
                formik.setFieldValue('userId', localAdmin._id || '');
                formik.setFieldValue('createdAt', localAdmin.createdAt || '');
                formik.setFieldValue('updatedAt', localAdmin.updatedAt || '');
            }

            // 2) Then fetch fresh data from API and override once it arrives
            try {
                setLoading(true);
                const response = await handleGetRequest(endPoints.getUserProfile(currentUserId), true);

                if (response?.data) {
                    const mergedUser = {
                        ...response.data,
                        // Prefer API timestamps, but fall back to local ones if API omits them
                        createdAt: response.data.createdAt || localAdmin?.createdAt,
                        updatedAt: response.data.updatedAt || localAdmin?.updatedAt
                    };

                    setUserData(mergedUser);
                    updateStoredAdminUserFields({
                        firstName: mergedUser.firstName || '',
                        lastName: mergedUser.lastName || '',
                        email: mergedUser.email || '',
                        whatsApp: mergedUser.whatsApp || '',
                        preferredLanguage: mergedUser.preferredLanguage || 'es'
                    });
                    formik.setFieldValue('firstName', mergedUser.firstName || '');
                    formik.setFieldValue('lastName', mergedUser.lastName || '');
                    formik.setFieldValue('email', mergedUser.email || '');
                    formik.setFieldValue('whatsApp', mergedUser.whatsApp || '');
                    formik.setFieldValue('preferredLanguage', mergedUser.preferredLanguage || 'es');
                    const apiRoleTitle =
                        typeof mergedUser.role === 'object' && mergedUser.role !== null ? mergedUser.role.title : '';
                    const newRoleTitle = apiRoleTitle || formik.values.role || localAdmin?.role?.title || '';
                    formik.setFieldValue('role', newRoleTitle);
                    formik.setFieldValue('isActive', mergedUser.isActive || '');
                    formik.setFieldValue('userId', mergedUser._id || '');
                    formik.setFieldValue('createdAt', mergedUser.createdAt || '');
                    formik.setFieldValue('updatedAt', mergedUser.updatedAt || '');
                }
            } finally {
                setLoading(false);
            }
        };

        const timer = setTimeout(() => {
            fetchUserData();
        }, 0);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserId]);

    const profileUser = userData || {};
    const roleTitle = formik.values.role || (profileUser?.role && profileUser.role.title) || '';

    const openPasswordDialog = () => {
        if (!profileUser?.email) {
            toast.error(t('Email not found for this user.'));
            return;
        }
        setPasswordStep('email');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordDialogVisible(true);
    };

    const closePasswordDialog = () => {
        if (!passwordLoading) {
            setPasswordDialogVisible(false);
        }
    };

    const handleSendOtp = async () => {
        if (!profileUser?.email) {
            toast.error(t('Email not found for this user.'));
            return;
        }

        try {
            setPasswordLoading(true);
            const payload = { email: profileUser.email };
            const response = await dispatch(handlePostRequest(payload, endPoints.generateOtp, true, false));

            if (response?.status === 201) {
                toast.success(response?.data?.message || t('OTP sent to your registered email.'));
                setPasswordStep('otp');
            }
        } catch (error) {
            // error toasts are handled in template
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (!profileUser?.email) {
            toast.error(t('Email not found for this user.'));
            return;
        }

        try {
            setPasswordLoading(true);
            const payload = { email: profileUser.email };
            const response = await dispatch(handlePostRequest(payload, endPoints.resendOtp, true, false));

            if (response?.status === 200) {
                toast.success(response?.data?.message || t('OTP resent successfully.'));
            }
        } catch (error) {
            // error toasts are handled in template
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otp || otp.trim().length === 0) {
            toast.error(t('Please enter the OTP sent to your email.'));
            return;
        }

        try {
            setPasswordLoading(true);
            const payload = { email: profileUser.email, otp: otp.trim() };
            const response = await dispatch(handlePostRequest(payload, endPoints.verifyOtp, true, false));

            if (response?.status === 200) {
                toast.success(response?.data?.message || t('OTP verified successfully.'));
                setPasswordStep('newPassword');
            }
        } catch (error) {
            // error toasts are handled in template
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleUpdatePassword = async () => {
        const trimmedNewPassword = newPassword.trim();
        const trimmedConfirmPassword = confirmPassword.trim();

        if (!trimmedNewPassword) {
            toast.error(t('Please enter a new password.'));
            return;
        }

        if (trimmedNewPassword.length < 8) {
            toast.error(t('Password must be at least 8 characters long.'));
            return;
        }

        if (trimmedNewPassword !== trimmedConfirmPassword) {
            toast.error(t('New password and confirm password do not match.'));
            return;
        }

        try {
            setPasswordLoading(true);
            const payload = {
                email: profileUser.email,
                password: trimmedNewPassword,
                // otp: otp.trim()
            };
            const response = await dispatch(handlePostRequest(payload, endPoints.changePassword, true, false));

            if (response?.status === 200) {
                toast.success(t('Password updated successfully.'));
                setPasswordDialogVisible(false);
            }
        } catch (error) {
            // error toasts are handled in template
        } finally {
            setPasswordLoading(false);
        }
    };

    const getInitials = () => {
        const firstName = profileUser.firstName || '';
        const lastName = profileUser.lastName || '';
        const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
        const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
        const initials = `${firstInitial}${lastInitial}`;
        return initials || t('U');
    };

    const getFullName = () => {
        const fullName = `${profileUser.firstName || ''} ${profileUser.lastName || ''}`.trim();
        return fullName || t('User');
    };

    const formatDateTime = (isoDate) => {
        if (!isoDate) return t('N/A');

        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return t('N/A');

        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const yyyy = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');

        let ampm = 'AM';
        if (hours >= 12) {
            ampm = 'PM';
            hours -= 12;
        }
        if (hours === 0) {
            hours = 12;
        }

        return `${dd}/${mm}/${yyyy} ${hours}:${minutes} ${ampm}`;
    };

    return (
        <div className="p-4">
            <div className="grid">
                <div className="col-12">
                    <h3 className="mb-4">{t('My Profile')}</h3>
                </div>

                {/* Profile Header Card */}
                <div className="col-12">
                    <div className="card" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                        <div className="flex align-items-center gap-4 p-4">
                            <div
                                style={{
                                    backgroundColor: '#0F8BFD',
                                    color: '#ffffff',
                                    fontSize: '2rem',
                                    fontWeight: 'bold',
                                    width: '100px',
                                    height: '100px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}
                            >
                                {getInitials()}
                            </div>
                            <div>
                                <h4 className="m-0 mb-2">{getFullName()}</h4>
                                <p className="text-500 m-0 mb-1">
                                    <i className="pi pi-envelope mr-2"></i>
                                    {profileUser.email || t('No email')}
                                </p>
                                <p className="text-500 m-0">
                                    <i className="pi pi-phone mr-2"></i>
                                    {profileUser.whatsApp || t('No phone')}
                                </p>
                                {roleTitle && (
                                    <div className="mt-2">
                                        <span className="p-tag p-tag-success">{roleTitle}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Profile Content Row: Edit Profile + Account Info with equal height */}
                <div className="col-12">
                    <div className="grid align-items-stretch">
                        {/* Profile Form Card */}
                        <div className="col-12 md:col-8">
                            <div className="card h-full flex flex-column" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                                <div className="card-header" style={{ borderBottom: '1px solid #e9ecef', padding: '1rem', marginBottom: '1rem' }}>
                                    <h5 className="m-0">{t('Edit Profile')}</h5>
                                </div>
                                <div className="card-body flex flex-column">
                                    <form onSubmit={formik.handleSubmit} className="flex flex-column h-full">
                                        <div className="grid p-fluid">
                                            <div className="col-12 md:col-6">
                                                <div className="field mb-3">
                                                    <label htmlFor="firstName" className="block mb-2">
                                                        {t('First Name')} <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <InputText
                                                        id="firstName"
                                                        name="firstName"
                                                        value={formik.values.firstName}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        className="w-full"
                                                        placeholder={t('Enter First Name')}
                                                        disabled={loading}
                                                    />
                                                    {formik.touched.firstName && formik.errors.firstName && <small className="p-error">{formik.errors.firstName}</small>}
                                                </div>
                                            </div>

                                            <div className="col-12 md:col-6">
                                                <div className="field mb-3">
                                                    <label htmlFor="lastName" className="block mb-2">
                                                        {t('Last Name')} <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <InputText
                                                        id="lastName"
                                                        name="lastName"
                                                        value={formik.values.lastName}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        className="w-full"
                                                        placeholder={t('Enter Last Name')}
                                                        disabled={loading}
                                                    />
                                                    {formik.touched.lastName && formik.errors.lastName && <small className="p-error">{formik.errors.lastName}</small>}
                                                </div>
                                            </div>

                                            <div className="col-12 md:col-6">
                                                <div className="field mb-3">
                                                    <label htmlFor="email" className="block mb-2">
                                                        {t('Email')} <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <InputText
                                                        id="email"
                                                        name="email"
                                                        type="email"
                                                        value={formik.values.email}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        className="w-full"
                                                        placeholder={t('Enter Email')}
                                                        disabled={loading}
                                                    />
                                                    {formik.touched.email && formik.errors.email && <small className="p-error">{formik.errors.email}</small>}
                                                </div>
                                            </div>

                                            <div className="col-12 md:col-6">
                                                <div className="field mb-3">
                                                    <label htmlFor="preferredLanguage" className="block mb-2">
                                                        {t('Preferred Language')}
                                                    </label>
                                                    <Dropdown
                                                        id="preferredLanguage"
                                                        name="preferredLanguage"
                                                        value={formik.values.preferredLanguage}
                                                        options={LANGUAGE_OPTIONS}
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        onChange={(e) => formik.setFieldValue('preferredLanguage', e.value)}
                                                        className="w-full"
                                                        disabled={loading}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-12 md:col-6">
                                                <div className="field mb-3">
                                                    <label htmlFor="whatsApp" className="block mb-2">
                                                        {t('Phone Number')} <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <InputText
                                                        id="whatsApp"
                                                        name="whatsApp"
                                                        value={formik.values.whatsApp}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        className="w-full"
                                                        placeholder={t('Enter Phone Number')}
                                                        disabled={loading}
                                                    />
                                                    {formik.touched.whatsApp && formik.errors.whatsApp && <small className="p-error">{formik.errors.whatsApp}</small>}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex justify-content-end gap-2 mt-4 mt-auto">
                                            <Button label={t('Update Profile')} icon="pi pi-save" type="submit" disabled={loading} className="p-button-success" />
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info Card */}
                        <div className="col-12 md:col-4">
                            <div className="card h-full flex flex-column" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                                <div className="card-header" style={{ borderBottom: '1px solid #e9ecef', padding: '1rem', marginBottom: '1rem' }}>
                                    <h5 className="m-0">{t('Account Information')}</h5>
                                </div>
                                <div className="card-body flex flex-column">
                                    <div className="flex flex-column gap-3">
                                        <div>
                                            <span className="text-500 block mb-1">{t('User ID')}</span>
                                            <strong>{profileUser._id || profileUser.id || t('N/A')}</strong>
                                        </div>
                                        <div>
                                            <span className="text-500 block mb-1">{t('Account Status')}</span>
                                            <span className="p-tag p-tag-success">{profileUser.isActive ? t('Active') : t('Inactive')}</span>
                                        </div>
                                        <div>
                                            <span className="text-500 block mb-1">{t('Created At')}</span>
                                            <strong>{formatDateTime(profileUser.createdAt)}</strong>
                                        </div>
                                        <div>
                                            <span className="text-500 block mb-1">{t('Last Updated At')}</span>
                                            <strong>{formatDateTime(profileUser.updatedAt)}</strong>
                                        </div>
                                        <div className="mt-4 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                                            <Button
                                                label={t('Update Password')}
                                                icon="pi pi-key"
                                                className="p-button-warning w-full"
                                                onClick={openPasswordDialog}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Dialog
                    header={t('Update Password')}
                    visible={passwordDialogVisible}
                    style={{ width: '30rem' }}
                    modal
                    onHide={closePasswordDialog}
                    closable={!passwordLoading}
                    dismissableMask={!passwordLoading}
                >
                    {passwordStep === 'email' && (
                        <div className="flex flex-column gap-3">
                            <div>
                                <span className="text-500 block mb-2">{t('Registered Email')}</span>
                                <InputText value={profileUser.email || ''} className="w-full" disabled />
                                <small className="text-500 block mt-1">{t("We'll send an OTP to this email to verify your identity.")}</small>
                            </div>
                            <div className="flex justify-content-center gap-2">
                                <Button
                                    label={t('Send OTP')}
                                    icon="pi pi-send"
                                    onClick={handleSendOtp}
                                    loading={passwordLoading}
                                    disabled={passwordLoading || !profileUser.email}
                                />
                            </div>
                        </div>
                    )}

                    {passwordStep === 'otp' && (
                        <div className="flex flex-column gap-3">
                            <div>
                                <span className="text-500 block mb-2">{t('Enter OTP')}</span>
                                <InputText
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    maxLength={6}
                                    className="w-full"
                                    placeholder={t('Enter 4-digit OTP')}
                                />
                                <small className="text-500 block mt-1">{t('Please enter the OTP sent to your registered email.')}</small>
                            </div>
                            <div className="flex justify-content-between gap-2">
                                <Button
                                    label={t('Back')}
                                    className="p-button-text"
                                    onClick={() => {
                                        if (!passwordLoading) setPasswordStep('email');
                                    }}
                                    disabled={passwordLoading}
                                />
                                <div className="flex gap-2">
                                    <Button
                                        label={t('Resend OTP')}
                                        className="p-button-text"
                                        icon="pi pi-refresh"
                                        onClick={handleResendOtp}
                                        disabled={passwordLoading}
                                    />
                                    <Button
                                        label={t('Verify OTP')}
                                        icon="pi pi-check"
                                        onClick={handleVerifyOtp}
                                        loading={passwordLoading}
                                        disabled={passwordLoading}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {passwordStep === 'newPassword' && (
                        <div className="flex flex-column gap-3">
                            <div>
                                <span className="text-500 block mb-2">{t('New Password')}</span>
                                <InputText
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full"
                                    placeholder={t('Enter new password')}
                                />
                            </div>
                            <div>
                                <span className="text-500 block mb-2">{t('Confirm Password')}</span>
                                <InputText
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full"
                                    placeholder={t('Re-enter new password')}
                                />
                            </div>
                            <div className="flex justify-content-between gap-2 mt-2">
                                <Button
                                    label={t('Back')}
                                    className="p-button-text"
                                    onClick={() => {
                                        if (!passwordLoading) setPasswordStep('otp');
                                    }}
                                    disabled={passwordLoading}
                                />
                                <Button
                                    label={t('Update Password')}
                                    icon="pi pi-save"
                                    onClick={handleUpdatePassword}
                                    loading={passwordLoading}
                                    disabled={passwordLoading}
                                />
                            </div>
                        </div>
                    )}
                </Dialog>
            </div>
        </div>
    );
};

export default Profile;
